import { hashKey, type Query, type QueryClient } from "@tanstack/react-query";
import type { VenueGroupConfig } from "../types/VenueGroup";
import type { VenueUserConfig } from "../types/VenueUser";
import {
  ORGANISATION_DATA_QUERY_ROOT,
  organisationDataQueryKey,
  OrganisationScopeUser,
  SELECTED_ORGANISATION_QUERY_ROOT,
  selectedOrganisationQueryKey
} from "../config/queryConfig";
import { withDerivedUserFullNames } from "./userFullName";

export type OrganisationData = {
  venueGroupConfig: VenueGroupConfig | null;
  venueUserConfig: VenueUserConfig | null;
};

type OrganisationDataResponse = {
  user?: VenueUserConfig | null;
  venueGroup?: VenueGroupConfig | null;
};

export const selectOrganisationData = (data: unknown): OrganisationData => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return { venueGroupConfig: null, venueUserConfig: null };
  }

  const organisationData = data as OrganisationDataResponse;
  const venueGroup = organisationData.venueGroup;

  return {
    venueGroupConfig: venueGroup?._id
      ? withDerivedUserFullNames(venueGroup)
      : null,
    venueUserConfig: organisationData.user ?? null
  };
};

const getVenueGroupId = (data: unknown): string | undefined => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return undefined;
  }

  const venueGroup = (data as { venueGroup?: unknown }).venueGroup;
  if (typeof venueGroup !== "object" || venueGroup === null) {
    return undefined;
  }

  const id = (venueGroup as { _id?: unknown })._id;
  return typeof id === "string" ? id : undefined;
};

export const findLatestCachedVenueGroupId = (
  queryClient: QueryClient
): string | undefined => {
  const queries = [
    ...queryClient
      .getQueryCache()
      .findAll({ queryKey: [ORGANISATION_DATA_QUERY_ROOT] }),
    ...queryClient
      .getQueryCache()
      .findAll({ queryKey: [SELECTED_ORGANISATION_QUERY_ROOT] })
  ];

  const latestQuery = queries.reduce<Query | undefined>((latest, query) => {
    if (!getVenueGroupId(query.state.data)) return latest;
    if (!latest || query.state.dataUpdatedAt > latest.state.dataUpdatedAt) {
      return query;
    }
    return latest;
  }, undefined);

  return getVenueGroupId(latestQuery?.state.data);
};

export const removeOtherOrganisationScopes = (
  queryClient: QueryClient,
  user: OrganisationScopeUser
): void => {
  const currentOrganisationKey = hashKey(organisationDataQueryKey(user));
  const currentSelectionKey = hashKey(selectedOrganisationQueryKey(user));

  queryClient.removeQueries({
    predicate: (query) => {
      const [root] = query.queryKey;
      if (root === ORGANISATION_DATA_QUERY_ROOT) {
        return hashKey(query.queryKey) !== currentOrganisationKey;
      }
      if (root === SELECTED_ORGANISATION_QUERY_ROOT) {
        return hashKey(query.queryKey) !== currentSelectionKey;
      }
      return false;
    }
  });
};
